import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ancestorIds, formatField, initials, subtreeIds } from "../../utils/orgChart";

/**
 * Панель подразделения справа (как в Битрикс24): люди, руководители, коммуникации.
 * Правка №9, [Claude] 2026-11-02. Кнопки правки — только при canEdit.
 */

const ROLE_BADGE = {
    head: "bg-amber-100 text-amber-800",
    deputy: "bg-sky-100 text-sky-800",
    member: "bg-slate-100 text-slate-600",
};

function PersonRow({ m, emp, roles, fields, canEdit, units, onUpdate, onRemove, unitName }) {
    const [title, setTitle] = useState(m.title || "");
    useEffect(() => setTitle(m.title || ""), [m.title]);
    const lines = fields.filter((f) => f !== "position").map((f) => formatField(f, emp)).filter(Boolean);
    const sub = fields.includes("position") ? (m.title || emp?.position) : null;
    return (
        <li className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid="org-person">
            <span className="mt-0.5 w-8 h-8 shrink-0 rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center">
                {initials(emp?.full_name)}
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <Link to={`/employees/${m.employee_id}`} className="text-sm font-semibold text-slate-800 truncate hover:underline">
                        {emp?.full_name || `#${m.employee_id}`}
                    </Link>
                    {m.role !== "member" && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${ROLE_BADGE[m.role]}`}>{roles[m.role]}</span>
                    )}
                </div>
                {unitName && <div className="text-[11px] text-slate-400 truncate">{unitName}</div>}
                {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
                {lines.map((l) => (
                    <div key={l} className="text-xs text-slate-500 truncate">{l}</div>
                ))}
                {canEdit && (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                        <select
                            value={m.role}
                            onChange={(e) => onUpdate(m.id, { role: e.target.value })}
                            className="text-xs border border-slate-200 rounded-md px-1 py-0.5 bg-white"
                            aria-label={`Роль: ${emp?.full_name}`}
                        >
                            {Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onBlur={() => { if ((m.title || "") !== title.trim()) onUpdate(m.id, { title }); }}
                            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                            placeholder={emp?.position || "должность здесь"}
                            className="flex-1 min-w-[100px] text-xs border border-slate-200 rounded-md px-1.5 py-0.5"
                            aria-label={`Должность в подразделении: ${emp?.full_name}`}
                        />
                        <select
                            value=""
                            onChange={(e) => e.target.value && onUpdate(m.id, { unit_id: Number(e.target.value) })}
                            className="text-xs border border-slate-200 rounded-md px-1 py-0.5 bg-white max-w-[110px]"
                            aria-label={`Перевести: ${emp?.full_name}`}
                        >
                            <option value="">Перевести…</option>
                            {units.filter((u) => u.id !== m.unit_id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                        <button
                            type="button"
                            onClick={() => onRemove(m)}
                            className="text-xs text-slate-400 hover:text-red-600 px-1"
                            aria-label={`Убрать из подразделения: ${emp?.full_name}`}
                        >
                            ✕
                        </button>
                    </div>
                )}
            </div>
        </li>
    );
}

export default function OrgUnitPanel({
    unit, tree, byUnit, employeesById, employees, roles, fields, canEdit, units, departments,
    onClose, onSelectUnit, onUpdateUnit, onAddMember, onUpdateMember, onRemoveMember,
    parentOptions = [], onMoveUnit, onDeleteUnit,
    extraLinks = [], linkOptions = [], onAddLink, onRemoveLink,
}) {
    const [tab, setTab] = useState("people");
    const [query, setQuery] = useState("");
    const [withSub, setWithSub] = useState(false);
    const [name, setName] = useState(unit.name);
    const [description, setDescription] = useState(unit.description || "");
    const [addQuery, setAddQuery] = useState("");
    const [addRole, setAddRole] = useState("member");

    useEffect(() => {
        setName(unit.name);
        setDescription(unit.description || "");
        setQuery("");
        setAddQuery("");
    }, [unit.id, unit.name, unit.description]);

    const unitNames = useMemo(() => new Map(units.map((u) => [u.id, u.name])), [units]);
    const path = ancestorIds(tree, unit.id);
    const scopeIds = withSub ? subtreeIds(tree, unit.id) : [unit.id];
    const all = scopeIds.flatMap((id) => byUnit.get(id) || []);
    const q = query.trim().toLowerCase().replace(/ё/g, "е");
    const match = (m) => {
        if (!q) return true;
        const e = employeesById.get(m.employee_id);
        return [e?.full_name, e?.position, m.title].some((v) => String(v || "").toLowerCase().replace(/ё/g, "е").includes(q));
    };
    const leaders = all.filter((m) => m.role !== "member" && match(m));
    const staff = all.filter((m) => m.role === "member" && match(m));
    const total = new Set(all.map((m) => m.employee_id)).size;

    const inUnit = new Set((byUnit.get(unit.id) || []).map((m) => m.employee_id));
    const aq = addQuery.trim().toLowerCase().replace(/ё/g, "е");
    const candidates = aq.length >= 2
        ? employees.filter((e) => !inUnit.has(e.id)
            && `${e.full_name} ${e.position || ""}`.toLowerCase().replace(/ё/g, "е").includes(aq)).slice(0, 8)
        : [];
    const department = departments.find((d) => d.id === unit.department_id);

    const saveName = () => {
        const v = name.trim();
        if (v && v !== unit.name) onUpdateUnit(unit.id, { name: v });
        else setName(unit.name);
    };

    const renderList = (list, empty) => (list.length ? (
        <ul>
            {list.map((m) => (
                <PersonRow
                    key={m.id}
                    m={m}
                    emp={employeesById.get(m.employee_id)}
                    roles={roles}
                    fields={fields}
                    canEdit={canEdit && m.unit_id === unit.id}
                    units={units}
                    onUpdate={onUpdateMember}
                    onRemove={onRemoveMember}
                    unitName={m.unit_id !== unit.id ? unitNames.get(m.unit_id) : null}
                />
            ))}
        </ul>
    ) : <p className="text-xs text-slate-400 py-1">{empty}</p>);

    return (
        <aside
            className="w-[380px] shrink-0 h-full bg-white border-l border-slate-200 flex flex-col"
            aria-label={`Подразделение ${unit.name}`}
            data-testid="org-panel"
        >
            <div className="px-4 pt-3 pb-2 border-b border-slate-100">
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                        {path.length > 0 && (
                            <div className="text-[11px] text-slate-400 truncate">
                                {path.map((id, i) => (
                                    <span key={id}>
                                        {i > 0 && " / "}
                                        <button type="button" className="hover:underline" onClick={() => onSelectUnit(id)}>{unitNames.get(id)}</button>
                                    </span>
                                ))}
                            </div>
                        )}
                        {canEdit ? (
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onBlur={saveName}
                                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                                className="w-full text-lg font-bold text-slate-900 border border-transparent hover:border-slate-200 focus:border-slate-300 rounded-md px-1 -mx-1"
                                aria-label="Название подразделения"
                            />
                        ) : (
                            <h2 className="text-lg font-bold text-slate-900 truncate">{unit.name}</h2>
                        )}
                    </div>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg leading-none px-1" aria-label="Закрыть панель">×</button>
                </div>
                {canEdit ? (
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onBlur={() => { if ((unit.description || "") !== description.trim()) onUpdateUnit(unit.id, { description }); }}
                        rows={description ? 2 : 1}
                        placeholder="Описание / функции подразделения"
                        className="mt-1 w-full text-xs text-slate-600 border border-slate-200 rounded-md px-2 py-1 resize-y"
                        aria-label="Описание подразделения"
                    />
                ) : unit.description ? <p className="mt-1 text-xs text-slate-600 whitespace-pre-line">{unit.description}</p> : null}
                {canEdit && (
                    <div className="mt-2 flex items-end gap-2">
                        <label className="flex-1 min-w-0 text-[11px] text-slate-500">
                            Подчиняется
                            <select
                                value={unit.parent_id ?? ""}
                                onChange={(e) => onMoveUnit?.(e.target.value === "" ? null : Number(e.target.value))}
                                className="mt-0.5 w-full text-sm border border-slate-200 rounded-md px-2 py-1 bg-white"
                                aria-label="Вышестоящее подразделение"
                            >
                                <option value="">— верхний уровень —</option>
                                {parentOptions.map((o) => <option key={o.id} value={o.id}>{"\u00a0\u00a0".repeat(o.depth)}{o.name}</option>)}
                            </select>
                        </label>
                        <button type="button" onClick={onDeleteUnit} className="shrink-0 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-md px-2 py-1.5" aria-label="Удалить подразделение">
                            Удалить
                        </button>
                    </div>
                )}
                {(canEdit || extraLinks.length > 0) && (
                    <div className="mt-2" data-testid="org-extra-links">
                        <div className="text-[11px] text-slate-500">Также подчиняется <span className="text-slate-400">(пунктир на схеме)</span></div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1">
                            {extraLinks.map((l) => (
                                <span key={l.id} className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-800 text-xs px-2 py-0.5">
                                    {l.name}
                                    {canEdit && (
                                        <button type="button" onClick={() => onRemoveLink?.(l.id)} className="text-violet-400 hover:text-violet-800" aria-label={`Убрать подчинение: ${l.name}`}>×</button>
                                    )}
                                </span>
                            ))}
                            {!extraLinks.length && <span className="text-xs text-slate-400">нет</span>}
                            {canEdit && linkOptions.length > 0 && (
                                <select value="" onChange={(e) => e.target.value && onAddLink?.(Number(e.target.value))}
                                    className="text-xs border border-slate-200 rounded-md px-1 py-0.5 bg-white max-w-[180px]" aria-label="Добавить подчинение">
                                    <option value="">+ ещё подчиняется…</option>
                                    {linkOptions.map((o) => <option key={o.id} value={o.id}>{"\u00a0\u00a0".repeat(o.depth)}{o.name}</option>)}
                                </select>
                            )}
                        </div>
                    </div>
                )}
                <div className="mt-2 flex gap-1">
                    <button
                        type="button"
                        onClick={() => setTab("people")}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold border ${tab === "people" ? "border-slate-800 text-slate-900" : "border-slate-200 text-slate-500"}`}
                    >
                        Сотрудники {total}
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("comms")}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold border ${tab === "comms" ? "border-slate-800 text-slate-900" : "border-slate-200 text-slate-500"}`}
                    >
                        Коммуникации
                    </button>
                </div>
            </div>

            {tab === "people" ? (
                <div className="flex-1 overflow-y-auto px-4 py-2">
                    <div className="flex items-center gap-2 mb-2">
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Найти по имени или должности"
                            className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-2 py-1"
                            aria-label="Поиск в подразделении"
                        />
                        {(tree.children.get(unit.id) || []).length > 0 && (
                            <label className="text-[11px] text-slate-500 flex items-center gap-1 whitespace-nowrap">
                                <input type="checkbox" checked={withSub} onChange={(e) => setWithSub(e.target.checked)} />
                                с подотделами
                            </label>
                        )}
                    </div>

                    {canEdit && (
                        <div className="mb-3 rounded-lg bg-sky-50 border border-sky-100 p-2">
                            <div className="flex gap-1">
                                <input
                                    value={addQuery}
                                    onChange={(e) => setAddQuery(e.target.value)}
                                    placeholder="Добавить сотрудника: начните вводить ФИО"
                                    className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
                                    aria-label="Добавить сотрудника"
                                />
                                <select
                                    value={addRole}
                                    onChange={(e) => setAddRole(e.target.value)}
                                    className="text-xs border border-slate-200 rounded-md px-1 bg-white"
                                    aria-label="Роль нового сотрудника"
                                >
                                    {Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                            </div>
                            {candidates.length > 0 && (
                                <ul className="mt-1 bg-white border border-slate-200 rounded-md max-h-48 overflow-y-auto">
                                    {candidates.map((e) => (
                                        <li key={e.id}>
                                            <button
                                                type="button"
                                                onClick={() => { onAddMember(unit.id, { employee_id: e.id, role: addRole }); setAddQuery(""); }}
                                                className="w-full text-left px-2 py-1 text-xs hover:bg-slate-50"
                                            >
                                                <b>{e.full_name}</b> <span className="text-slate-400">{e.position || ""} {e.department ? `· ${e.department}` : ""}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mt-1">Руководители {leaders.length}</h3>
                    {renderList(leaders, "Руководитель не назначен")}
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mt-3">Подчинённые {staff.length}</h3>
                    {renderList(staff, canEdit ? "Добавьте сотрудников через поле выше" : "Нет сотрудников")}
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto px-4 py-3 text-sm">
                    {department ? (
                        <p className="text-slate-700">
                            Общие контакты отдела «{department.name}» (Telegram, телефон, почта):{" "}
                            <Link className="text-sky-700 underline" to={`/organization/departments/${department.id}`}>открыть</Link>
                        </p>
                    ) : (
                        <p className="text-slate-500">К подразделению не привязаны общие контакты отдела.</p>
                    )}
                    {canEdit && (
                        <label className="block mt-3 text-xs text-slate-600">
                            Общие контакты брать из отдела
                            <select
                                value={unit.department_id || ""}
                                onChange={(e) => onUpdateUnit(unit.id, { department_id: e.target.value ? Number(e.target.value) : null })}
                                className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-1 bg-white"
                                aria-label="Отдел для общих контактов"
                            >
                                <option value="">— не привязано —</option>
                                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </label>
                    )}
                </div>
            )}
        </aside>
    );
}
